import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ValidationManager, sendRioEvent, parseString, SpecType, SubType, ElementType } from '@chegg/commerce-utils';
import { FloatingInputWrapper } from '@chegg-ui/input-wrapper';
import { useAppSelector } from '../../hooks/useAppSelector';
import { configSelector } from '../../redux/ConfigState/ConfigState.selectors';
import { paymentSelector } from '../../redux/PaymentState/PaymentState.selectors';
import { getString } from '../../constants/Strings';
import { updateFormFieldData } from '../../redux/PaymentState/PaymentState.actions';
import { DataTestAttributes } from '../../constants/DataTestAttributes';
import { useCreditCardForm } from '../../business/processors/recurly/CreditCardService';
import CVVTooltip from '../PaymentForm/CVVTooltip';
import MemoizedUserAddress from '../UserAddress';
import { PaymentFormDataFields, PaymentFormValidationFields } from '../UserAddress/constants';
import { FormHeader } from '../PaymentForm';
import { FormContainer, FormWrapper, Row, StyledRequiredFieldsLegend } from '../PaymentForm/styled';
import { getPaymentFieldErrorText, getPaymentFieldLabelText } from '../PaymentForm/helpers';
import { InputWrapper, GlobalStyle, CVVInputWrapper } from './styled';
import { CardNumberElement, CardMonthElement, CardYearElement, CardCvvElement, RiskDataCollector } from '@recurly/react-recurly';
import { Zipcode } from '../../constants/AppConstants';
import { sendNewRelicMessage } from '../../utils/NewRelicUtils';
import { sendRioEventCallback } from '../../utils/CallbackUtils';
var _ValidationManager$ty = ValidationManager.types,
  CCFIELDS = _ValidationManager$ty.CCFIELDS,
  VALIDATION_TYPES = _ValidationManager$ty.VALIDATION_TYPES;
export var getCardholderNameError = function getCardholderNameError(name) {
  return name ? '' : ValidationManager.getMessage(VALIDATION_TYPES.REQUIRED, CCFIELDS.CARDHOLDER_NAME);
};
var CreditCardForm = function CreditCardForm(_ref) {
  var _ref$isEdit = _ref.isEdit,
    isEdit = _ref$isEdit === void 0 ? false : _ref$isEdit;
  var _useAppSelector = useAppSelector(configSelector),
    isPrimary = _useAppSelector.isPrimary,
    paymentForm = _useAppSelector.paymentForm,
    paymentErrorModal = _useAppSelector.paymentErrorModal,
    submit = _useAppSelector.submit;
  var _useAppSelector2 = useAppSelector(paymentSelector),
    formFields = _useAppSelector2.formFields,
    rendersRecurlyForm = _useAppSelector2.rendersRecurlyForm,
    _useAppSelector2$bill = _useAppSelector2.billingAddress,
    city = _useAppSelector2$bill.city,
    state = _useAppSelector2$bill.state,
    isZipMandatory = _useAppSelector2$bill.isZipMandatory;
  var dispatch = useDispatch();
  var _useSelector = useSelector(configSelector),
    country = _useSelector.country;
  var _useCreditCardForm = useCreditCardForm(),
    formRef = _useCreditCardForm.formRef,
    handleSubmit = _useCreditCardForm.handleSubmit,
    getElementEventListeners = _useCreditCardForm.getElementEventListeners;
  var _useState = useState(rendersRecurlyForm !== null && rendersRecurlyForm !== void 0 ? rendersRecurlyForm : true),
    _useState2 = _slicedToArray(_useState, 2),
    renderRecurlyForm = _useState2[0],
    setRenderRecurlyForm = _useState2[1];

  // Recurly requires us to separate name into two inputs: first name and last name
  var _split = formFields[CCFIELDS.CARDHOLDER_NAME].value.split(' '),
    _split2 = _slicedToArray(_split, 2),
    firstName = _split2[0],
    lastName = _split2[1];
  var handleCardholderNameFocus = function handleCardholderNameFocus() {
    // We need to add a riohandler on the name part of the form seperately here
    sendRioEvent({
      info: parseString(getString("APP.RIO.EVENTS.EL_NAME.USER_ENTERS_FIELD_ADD"), {
        fieldType: 'cardholderName'
      }),
      specType: SpecType.INTERACTION,
      subType: SubType.TEXT,
      elementType: ElementType.TEXT,
      callback: sendRioEventCallback
    });
    dispatch(updateFormFieldData(_defineProperty({}, CCFIELDS.CARDHOLDER_NAME, {
      focused: true,
      value: formFields[CCFIELDS.CARDHOLDER_NAME].value,
      error: ''
    })));
  };
  var handleCardholderNameBlur = function handleCardholderNameBlur(_ref2) {
    var value = _ref2.target.value;
    dispatch(updateFormFieldData(_defineProperty({}, CCFIELDS.CARDHOLDER_NAME, {
      focused: false,
      empty: !value,
      value: value,
      error: getCardholderNameError(value)
    })));
  };
  useEffect(function () {
    if (submit) {
      handleSubmit(isPrimary !== null && isPrimary !== void 0 ? isPrimary : true);
    }
  }, [submit, isPrimary]);

  // To reset the Recurly form, we first set rendersRecurlyForm to false to remove the CC elements
  // Then we re-render the CC elements by resetting rendersRecurlyForm to true
  useEffect(function () {
    setRenderRecurlyForm(rendersRecurlyForm !== null && rendersRecurlyForm !== void 0 ? rendersRecurlyForm : true);
    if (!rendersRecurlyForm) {
      setRenderRecurlyForm(true);
    }
  }, [rendersRecurlyForm]);
  if (!renderRecurlyForm) {
    return null;
  }

  // we render hidden input elements that Recurly.js will reference during tokenization
  // Billing address requirements to log "-" for empty address1, city and state
  var hiddenRecurlyInputs = _objectSpread(_objectSpread({
    first_name: firstName,
    last_name: lastName || 'customer',
    city: city || '-',
    state: state || '-'
  }, !isZipMandatory && {
    postal_code: country !== 'GB' ? Zipcode.ABCDE : Zipcode.GB
  }), {}, {
    address1: '-'
  });
  return /*#__PURE__*/React.createElement(FormWrapper, {
    id: 'pm-payment-form',
    "data-test": DataTestAttributes.CREDIT_CARD_FORM,
    ref: formRef
  }, /*#__PURE__*/React.createElement(RiskDataCollector, {
    strategy: "kount",
    onError: function onError(error) {
      return sendNewRelicMessage("Risk data collector error: ".concat(error.message));
    }
  }), /*#__PURE__*/React.createElement(GlobalStyle, {
    formFields: formFields
  }), /*#__PURE__*/React.createElement(FormHeader, {
    isEdit: isEdit
  }), /*#__PURE__*/React.createElement(FormContainer, {
    "data-test": DataTestAttributes.CREDIT_CARD_FORM_CONTAINER,
    role: 'group',
    "aria-labelledby": 'pm-payment-form-header'
  }, /*#__PURE__*/React.createElement("fieldset", null, /*#__PURE__*/React.createElement(StyledRequiredFieldsLegend, null, getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.ALL_REQUIRED_FIELDS) || getString('APP.FORM.LEGEND.REQUIRED_FIELDS')), /*#__PURE__*/React.createElement(FloatingInputWrapper, {
    labelText: getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.NAME_ON_THE_CARD) || getString('APP.FORM.LABEL.NAME'),
    inputId: CCFIELDS.CARDHOLDER_NAME,
    isFloating: formFields[CCFIELDS.CARDHOLDER_NAME].focused || !Boolean(formFields.cardholderName.empty),
    errorText: getPaymentFieldErrorText(paymentForm, paymentErrorModal, formFields[CCFIELDS.CARDHOLDER_NAME].error, PaymentFormValidationFields.REQUIRED_CARD_NAME, PaymentFormValidationFields.INVALID_CARD_NAME) || formFields[CCFIELDS.CARDHOLDER_NAME].error
  }, /*#__PURE__*/React.createElement(InputWrapper, {
    onFocus: handleCardholderNameFocus,
    onBlur: handleCardholderNameBlur,
    id: CCFIELDS.CARDHOLDER_NAME
    // @ts-ignore
    ,
    error: formFields[CCFIELDS.CARDHOLDER_NAME].error,
    "data-test": "".concat(CCFIELDS.CARDHOLDER_NAME, "_input")
  })), !isEdit && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FloatingInputWrapper, {
    labelText: getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.CARD_NUMBER) || getString('APP.FORM.LABEL.NUMBER'),
    inputId: CCFIELDS.CARD_NUMBER,
    isFloating: formFields[CCFIELDS.CARD_NUMBER].focused || !Boolean(formFields.number.empty),
    errorText: getPaymentFieldErrorText(paymentForm, paymentErrorModal, formFields[CCFIELDS.CARD_NUMBER].error, PaymentFormValidationFields.REQUIRED_CARD_NUMBER, PaymentFormValidationFields.INVALID_CARD_NUMBER) || formFields[CCFIELDS.CARD_NUMBER].error
  }, /*#__PURE__*/React.createElement(CardNumberElement, getElementEventListeners(CCFIELDS.CARD_NUMBER)))), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(FloatingInputWrapper, {
    helperText: getString('APP.FORM.LABEL.EXPIRATION.MONTH.HELPER.TEXT'),
    labelText: getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.EXPIRATION_MONTH) || getString('APP.FORM.LABEL.EXPIRATION.MONTH'),
    inputId: CCFIELDS.EXPIRATION_MONTH,
    isFloating: formFields[CCFIELDS.EXPIRATION_MONTH].focused || !Boolean(formFields.expirationMonth.empty),
    errorText: getPaymentFieldErrorText(paymentForm, paymentErrorModal, formFields[CCFIELDS.EXPIRATION_MONTH].error, PaymentFormValidationFields.REQUIRED_EXPIRATION_DATE, PaymentFormValidationFields.INVALID_EXPIRATION_DATE) || formFields[CCFIELDS.EXPIRATION_MONTH].error
  }, /*#__PURE__*/React.createElement(CardMonthElement, getElementEventListeners(CCFIELDS.EXPIRATION_MONTH))), /*#__PURE__*/React.createElement(FloatingInputWrapper, {
    helperText: getString('APP.FORM.LABEL.EXPIRATION.YEAR.HELPER.TEXT'),
    labelText: getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.EXPIRATION_YEAR) || getString('APP.FORM.LABEL.EXPIRATION.YEAR'),
    inputId: CCFIELDS.EXPIRATION_YEAR,
    isFloating: formFields[CCFIELDS.EXPIRATION_YEAR].focused || !Boolean(formFields.expirationYear.empty),
    errorText: getPaymentFieldErrorText(paymentForm, paymentErrorModal, formFields[CCFIELDS.EXPIRATION_YEAR].error, PaymentFormValidationFields.REQUIRED_EXPIRATION_DATE, PaymentFormValidationFields.INVALID_EXPIRATION_DATE) || formFields[CCFIELDS.EXPIRATION_YEAR].error
  }, /*#__PURE__*/React.createElement(CardYearElement, getElementEventListeners(CCFIELDS.EXPIRATION_YEAR))), /*#__PURE__*/React.createElement(FloatingInputWrapper, {
    trailingIcon: /*#__PURE__*/React.createElement(CVVTooltip, null),
    trailingIconClickable: true,
    labelText: getPaymentFieldLabelText(paymentForm, PaymentFormDataFields.CVV) || getString('APP.FORM.LABEL.CVV'),
    inputId: CCFIELDS.CVV,
    isFloating: formFields[CCFIELDS.CVV].focused || !Boolean(formFields.cvv.empty),
    errorText: getPaymentFieldErrorText(paymentForm, paymentErrorModal, formFields[CCFIELDS.CVV].error, PaymentFormValidationFields.REQUIRED_CVV, PaymentFormValidationFields.INVALID_CVV) || formFields[CCFIELDS.CVV].error
  }, /*#__PURE__*/React.createElement(CVVInputWrapper, null, /*#__PURE__*/React.createElement(CardCvvElement, getElementEventListeners(CCFIELDS.CVV))))), /*#__PURE__*/React.createElement(MemoizedUserAddress, null), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "recurly-token",
    "data-recurly": "token"
  }), Object.entries(hiddenRecurlyInputs).map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
      key = _ref4[0],
      value = _ref4[1];
    return /*#__PURE__*/React.createElement("input", {
      type: "hidden",
      "data-recurly": key,
      value: value
    });
  }))));
};
export default CreditCardForm;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/payment-methods/dist/esm/internals/Recurly/CreditCardForm.js
// module id = 4619
// module chunks = 11